import { useMemo } from 'react';
import {
  collection,
  collectionGroup,
  CollectionReference,
  getFirestore,
  limit,
  orderBy,
  query,
} from 'firebase/firestore';
import {
  Deployment,
  EnvironmentOperationalStatus,
  EnvironmentStatus,
  SetupTask,
  TestExecution,
} from '@finvari/deliver';
import { useDeploymentStatus } from '../contexts/DeploymentStatusContext';
import { useConfig } from '../contexts/ConfigContext';
import { useConfiguredEnvironments, isEnvironmentVisibleTo } from '../contexts/EnvironmentsContext';
import { useSubscribedList } from '../utils/firebaseHooks';

// Fetch all recent setup tasks across all rollouts
function useAllSetupTasks(n = 100) {
  const setupTasksQuery = useMemo(() => {
    const db = getFirestore();
    const setupTasksRef = collectionGroup(db, 'setupTasks') as CollectionReference<SetupTask>;
    return query(setupTasksRef, orderBy('createdAt', 'desc'), limit(n));
  }, [n]);
  return useSubscribedList<SetupTask, 'setupTasks'>(setupTasksQuery, 'setupTasks');
}

// Fetch all recent test executions
function useAllTestExecutions(n = 100) {
  const testExecutionsQuery = useMemo(() => {
    const db = getFirestore();
    const testExecutionsRef = collection(db, 'testExecutions') as CollectionReference<TestExecution>;
    return query(testExecutionsRef, orderBy('createdAt', 'desc'), limit(n));
  }, [n]);
  return useSubscribedList<TestExecution, 'testExecutions'>(testExecutionsQuery, 'testExecutions');
}

// Compute operational status for an environment
// Uses only the most recent deployment per module, setup tasks for active rollout,
// and most recent test execution per suite
function computeOperationalStatus(
  environmentId: string,
  liveModuleDeployments: Map<string, Deployment> | undefined,
  allDeployments: Deployment[],
  setupTasks: SetupTask[],
  testExecutions: TestExecution[],
  activeRolloutId: string | undefined
): {
  status: EnvironmentOperationalStatus;
  lastSetupTaskStatus: SetupTask['status'] | null;
  lastTestStatus: TestExecution['status'] | null;
} {
  // Check deployments - use live module deployments (most recent per module) for status,
  // but also check all recent deployments for in-progress state
  const liveDeployments = liveModuleDeployments
    ? Array.from(liveModuleDeployments.values())
    : [];

  // Check if any deployment for this environment is currently in progress
  // (could be a new deployment that hasn't finished yet)
  const envDeployments = allDeployments.filter(d => d.environment === environmentId);
  const isDeploying = envDeployments.some(
    d => d.status === 'in_progress' || d.status === 'pending'
  );

  // For failed/success status, only look at the live (most recent per module) deployments
  const hasDeploymentFailed = liveDeployments.some(d => d.status === 'failed');
  const allDeploymentsSuccessful = liveDeployments.length > 0 &&
    liveDeployments.every(d => d.status === 'success');

  // Get setup tasks for this environment's active rollout only
  // Tasks are ordered by createdAt desc, so first one is most recent
  const envSetupTasks = activeRolloutId
    ? setupTasks.filter(t => t.rolloutId === activeRolloutId)
    : [];
  const hasSetupTaskInFlight = envSetupTasks.some(
    t => t.status === 'pending' || t.status === 'in_progress' || t.status === 'awaiting_deploy'
  );
  const lastSetupTaskStatus = envSetupTasks[0]?.status ?? null;

  // Get the most recent test execution per suite for this environment's active rollout
  // testExecutions are ordered by createdAt desc
  const envTestExecutions = testExecutions.filter(t =>
    t.environment === environmentId && (!activeRolloutId || t.rolloutId === activeRolloutId)
  );
  const mostRecentPerSuite = new Map<string, TestExecution>();
  for (const exec of envTestExecutions) {
    if (!mostRecentPerSuite.has(exec.testSuiteId)) {
      mostRecentPerSuite.set(exec.testSuiteId, exec);
    }
  }
  const recentTestExecutions = Array.from(mostRecentPerSuite.values());

  // Check if any of the most recent test executions (per suite) are in flight
  const hasTestInFlight = recentTestExecutions.some(
    t => t.status === 'pending' || t.status === 'in_progress'
  );
  // Get the overall most recent test execution status
  const lastTestStatus = envTestExecutions[0]?.status ?? null;

  // Compute status with priority order: deploying > failed > preparing > testing > ready > idle
  let status: EnvironmentOperationalStatus;
  if (isDeploying) {
    status = 'deploying';
  } else if (hasDeploymentFailed || lastSetupTaskStatus === 'failed') {
    status = 'failed';
  } else if (hasSetupTaskInFlight) {
    status = 'preparing';
  } else if (hasTestInFlight) {
    status = 'testing';
  } else if (allDeploymentsSuccessful) {
    status = 'ready';
  } else {
    status = 'idle';
  }

  return { status, lastSetupTaskStatus, lastTestStatus };
}

export interface UseEnvironmentsResult {
  environments: EnvironmentStatus[];
  loading: boolean;
  error: string | null;
}

/**
 * Internal: every configured environment with full operational status, unfiltered.
 * `useEnvironments` filters this to the visible set; `useEnvironment` resolves against it directly
 * so direct navigation to a hidden (personal) environment still works.
 */
function useAllEnvironmentStatuses(): UseEnvironmentsResult {
  const { userProfile, loading: configLoading, error: configError } = useConfig();
  const { environments: environmentDocs, loading: environmentsLoading, error: environmentsError } = useConfiguredEnvironments();
  const {
    liveDeploymentsByEnvironment,
    deployments,
    loading: deploymentLoading,
    error: deploymentError,
  } = useDeploymentStatus();

  const { setupTasks, loading: setupLoading, error: setupError } = useAllSetupTasks();
  const { testExecutions, loading: testLoading, error: testError } = useAllTestExecutions();

  const environments = useMemo((): EnvironmentStatus[] => {
    const result: EnvironmentStatus[] = environmentDocs.map(environmentDoc => {
      const environmentId = environmentDoc.gcpProjectId;
      const liveDeployment = liveDeploymentsByEnvironment.get(environmentId);
      // The live rollout drives operational-status filtering (setup tasks / tests of the running
      // rollout); the persisted activeRollout denorm is what the UI displays as the current rollout.
      const activeRolloutId = liveDeployment?.rolloutId;

      // Get the most recent deployment for lastUpdated
      const envDeployments = deployments.filter(d => d.environment === environmentId);
      let lastUpdated = new Date().toISOString();
      if (envDeployments.length > 0) {
        const mostRecent = envDeployments.reduce((latest, d) =>
          new Date(d.createdAt) > new Date(latest.createdAt) ? d : latest
        );
        lastUpdated = mostRecent.finishedAt || mostRecent.createdAt;
      }

      // Compute operational status using live module deployments (most recent per module)
      const { status, lastSetupTaskStatus, lastTestStatus } = computeOperationalStatus(
        environmentId,
        liveDeployment?.deployments,
        deployments,
        setupTasks,
        testExecutions,
        activeRolloutId
      );

      return {
        environmentId,
        environmentName: environmentDoc.name || environmentId,
        gcpProjectId: environmentDoc.gcpProjectId,
        ownerUid: environmentDoc.ownerUid,
        archived: environmentDoc.archived,
        poolId: environmentDoc.poolId,
        lease: environmentDoc.lease,
        description: environmentDoc.description,
        currentDeployment: null,
        lastDeployment: null,
        status,
        activeRollout: environmentDoc.activeRollout,
        lastUpdated,
        createdAt: lastUpdated,
        lastSetupTaskStatus,
        lastTestStatus,
      };
    });

    // Sort alphabetically, then promote the user's default to the top
    result.sort((a, b) => a.environmentName.localeCompare(b.environmentName));

    const defaultId = userProfile?.defaultEnvironmentId;
    if (defaultId) {
      const defaultIdx = result.findIndex(e => e.environmentId === defaultId);
      if (defaultIdx > 0) {
        result.unshift(result.splice(defaultIdx, 1)[0]);
      }
    }

    return result;
  }, [environmentDocs, userProfile, liveDeploymentsByEnvironment, deployments, setupTasks, testExecutions]);

  return {
    environments,
    loading: configLoading || environmentsLoading || deploymentLoading || setupLoading || testLoading,
    error: configError || environmentsError || deploymentError || setupError || testError || null,
  };
}

/**
 * Hook to get environments with full operational status. By default it's filtered to those visible
 * to the current user (personal dev environments owned by others are hidden); pass includeHidden to
 * return the full set. This is the list/picker-facing hook.
 */
export function useEnvironments(includeHidden = false): UseEnvironmentsResult {
  const { userProfile } = useConfig();
  const uid = userProfile?.uid;
  const { environments, loading, error } = useAllEnvironmentStatuses();
  const visible = useMemo(
    () => (includeHidden ? environments : environments.filter((env) => isEnvironmentVisibleTo(env, uid))),
    [environments, uid, includeHidden]
  );
  return { environments: visible, loading, error };
}

export interface UseEnvironmentResult {
  environment: EnvironmentStatus | undefined;
  loading: boolean;
  error: string | null;
  // Convenience flags for button disabling
  hasAnyOperationInFlight: boolean;
  isDeploying: boolean;
  hasSetupTaskInFlight: boolean;
  hasTestInFlight: boolean;
}

/**
 * Hook to get a single environment with full operational status.
 * This uses the same logic as useEnvironments and filters to a single result.
 */
export function useEnvironment(environmentId: string | undefined): UseEnvironmentResult {
  // Resolve against the unfiltered set so a direct link to a hidden (personal) environment works.
  const { environments, loading, error } = useAllEnvironmentStatuses();

  const environment = useMemo(() => {
    if (!environmentId) return undefined;
    return environments.find(env => env.environmentId === environmentId);
  }, [environments, environmentId]);

  // Compute convenience flags from status
  const isDeploying = environment?.status === 'deploying';
  const hasSetupTaskInFlight = environment?.status === 'preparing';
  const hasTestInFlight = environment?.status === 'testing';
  const hasAnyOperationInFlight = isDeploying || hasSetupTaskInFlight || hasTestInFlight;

  return {
    environment,
    loading,
    error,
    hasAnyOperationInFlight,
    isDeploying,
    hasSetupTaskInFlight,
    hasTestInFlight,
  };
}
